import { useState } from "react";
import { FileText, Link2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lovable-demo/runtime/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lovable-demo/lib/warranty/calc";
import {
  evidenceFilePath,
  evidenceProvenance,
  PROVENANCE_LABEL,
  validateAttachment,
  type EvidenceAttachment,
} from "@/lovable-demo/lib/warranty/evidence-attach";
import type { GovernanceResult } from "@/lovable-demo/lib/warranty/store";
import type { EvidenceItem } from "@/lovable-demo/lib/warranty/types";

export const EVIDENCE_BUCKET = "record-evidence";

/** Attach button + dialog requiring a file upload or a reference. */
export function AttachEvidenceButton({
  recordId,
  gateId,
  requirement,
  onAttach,
  stageOnly = false,
}: {
  recordId: string;
  gateId: string;
  requirement: string;
  onAttach: (attachment: EvidenceAttachment) => Promise<GovernanceResult>;
  /** Package #2.7 — evidence is held and recorded with a later governed write. */
  stageOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"file" | "reference">("file");
  const [file, setFile] = useState<File | null>(null);
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setReference("");
    setDescription("");
    setError(null);
    setMode("file");
  };

  const submit = async () => {
    setError(null);
    let attachment: EvidenceAttachment;
    if (mode === "file") {
      if (!file) return setError("Choose a file to upload.");
      attachment = { kind: "file", fileName: file.name, filePath: "pending" };
    } else {
      attachment = { kind: "reference", reference, description };
    }
    const invalid = validateAttachment(attachment);
    if (invalid) return setError(invalid);
    setBusy(true);
    try {
      if (mode === "file" && file) {
        const path = evidenceFilePath(recordId, gateId, file.name, String(Date.now()));
        const up = await supabase.storage
          .from(EVIDENCE_BUCKET)
          .upload(path, file, { upsert: false });
        if (up.error) {
          setError(`The file could not be uploaded: ${up.error.message}. Nothing was attached.`);
          return;
        }
        attachment = { kind: "file", fileName: file.name, filePath: path };
      }
      const result = await onAttach(attachment);
      if (result.ok) {
        if (stageOnly)
          toast.message("Evidence reference added", {
            description: "It is recorded only when the verdict is recorded.",
          });
        else toast.success("Evidence attached and confirmed", { description: requirement });
        setOpen(false);
        reset();
      } else {
        setError(result.error);
        toast.error("Evidence not attached", { description: result.error });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 px-2 text-[0.68rem] text-primary"
        onClick={() => setOpen(true)}
      >
        <Paperclip className="size-3" /> {stageOnly ? "add evidence (optional)" : "attach"}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (busy) return;
          setOpen(o);
          if (!o) reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attach evidence</DialogTitle>
            <DialogDescription>
              {requirement}. Upload a file or give a reference. Broadview records who attached it
              and when, and confirms the audit entry before the item counts toward the gate.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            {(["file", "reference"] as const).map((m) => (
              <Button
                key={m}
                type="button"
                size="sm"
                variant={mode === m ? "default" : "outline"}
                className="h-7 text-xs"
                onClick={() => {
                  setMode(m);
                  setError(null);
                }}
              >
                {m === "file" ? <FileText className="size-3" /> : <Link2 className="size-3" />}
                {m === "file" ? "Upload file" : "Reference"}
              </Button>
            ))}
          </div>
          {mode === "file" ? (
            <div className="space-y-1.5">
              <label htmlFor={`ev-file-${gateId}`} className="text-xs text-muted-foreground">
                Synthetic file (saved only in this browser, max 2 MB)
              </label>
              <Input
                id={`ev-file-${gateId}`}
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <div className="space-y-1.5">
                <label htmlFor={`ev-ref-${gateId}`} className="text-xs text-muted-foreground">
                  URL or document / system reference ID
                </label>
                <Input
                  id={`ev-ref-${gateId}`}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="e.g. PLM-ECN-4471 or https://…"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor={`ev-desc-${gateId}`} className="text-xs text-muted-foreground">
                  Short description
                </label>
                <Textarea
                  id={`ev-desc-${gateId}`}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-16 text-xs"
                />
              </div>
            </div>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={submit}>
              {busy ? "Recording…" : "Attach evidence"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Provenance line shown under an attached item. */
export function EvidenceMeta({ item }: { item: EvidenceItem }) {
  const prov = evidenceProvenance(item);
  const openFile = async () => {
    if (!item.filePath) return;
    const { data, error } = await supabase.storage
      .from(EVIDENCE_BUCKET)
      .createSignedUrl(item.filePath, 300);
    if (error || !data) toast.error("File could not be opened");
    else {
      const link = document.createElement("a");
      link.href = data.signedUrl;
      link.download = item.fileName ?? "synthetic-evidence";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };
  const by = item.attachedByTitle ? `${item.addedBy}, ${item.attachedByTitle}` : item.addedBy;
  return (
    <span className="num block text-[0.64rem] text-muted-foreground">
      <span
        className={cn(
          "mr-1 rounded border px-1 py-px text-[0.58rem] uppercase",
          prov === "legacy" ? "border-warning/40 text-warning" : "border-border",
        )}
      >
        {PROVENANCE_LABEL[prov]}
      </span>
      {prov === "file" && (
        <button type="button" onClick={openFile} className="text-primary hover:underline">
          Download {item.fileName}
        </button>
      )}
      {prov === "reference" && (
        <>
          {item.reference} — {item.description}
        </>
      )}
      {prov === "seeded" || prov === "legacy" ? item.type : null} · {by} · {fmtDate(item.addedAt)}
    </span>
  );
}
